import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, FileUp, ArrowLeft, Euro, Type, AlignLeft, PlusCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const errMsg = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join("; ") || fallback;
  return fallback;
};

// Payload pulito per tipo: evita di inviare campi solo-display (es. price come etichetta "€6,50" nei rimossi)
const cleanOp = (it) => {
  if (it.kind === "price") return { kind: "price", dish_id: it.dish_id, price: it.price ?? null, formats: it.formats || [] };
  if (it.kind === "name") return { kind: "name", dish_id: it.dish_id, new_name: it.new_name };
  if (it.kind === "desc") return { kind: "desc", dish_id: it.dish_id, new_desc: it.new_desc };
  if (it.kind === "new") return { kind: "new", category_id: it.category_id ?? null, category_name: it.category_name ?? null, type: it.type ?? null, name: it.name, description: it.description ?? "", price: (it.formats && it.formats.length) ? null : (it.price ?? null), formats: it.formats || [] };
  if (it.kind === "remove") return { kind: "remove", dish_id: it.dish_id };
  return it;
};

// Flusso ISOLATO "Aggiorna da PDF": agisce solo sul menu selezionato.
const GROUPS = [
  { key: "prices", label: "Prezzi cambiati", icon: Euro, color: "text-emerald-600" },
  { key: "names", label: "Nomi cambiati", icon: Type, color: "text-blue-600" },
  { key: "descriptions", label: "Descrizioni cambiate", icon: AlignLeft, color: "text-violet-600" },
  { key: "new", label: "Piatti nuovi", icon: PlusCircle, color: "text-[#002FA7]" },
  { key: "removed", label: "Piatti rimossi", icon: Trash2, color: "text-red-600" },
];

const PdfUpdateDialog = ({ open, onClose, onApplied, client, base, menuId: fixedMenuId, menus: menusProp, fetchMenusUrl, title }) => {
  const [menuId, setMenuId] = useState(fixedMenuId || null);
  const [menus, setMenus] = useState(menusProp || []);
  const [step, setStep] = useState(fixedMenuId ? 1 : 0);
  const [loading, setLoading] = useState(false);
  const [diff, setDiff] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!fixedMenuId && fetchMenusUrl) {
      client.get(fetchMenusUrl).then((r) => setMenus(r.data || [])).catch(() => setMenus([]));
    }
  }, [fetchMenusUrl, fixedMenuId, client]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("include_descriptions", "true");
      const res = await client.post(`${base}/${menuId}/update-pdf/analyze`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = res.data;
      const all = new Set();
      GROUPS.forEach((g) => (d[g.key] || []).forEach((it) => all.add(it.id)));
      setDiff(d);
      setSelected(all);
      setStep(2);
    } catch (err) {
      toast.error(errMsg(err, "Errore nell'analisi del PDF"));
    } finally { setLoading(false); e.target.value = ""; }
  };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const totalChanges = useMemo(() => diff ? GROUPS.reduce((s, g) => s + (diff[g.key]?.length || 0), 0) : 0, [diff]);

  const apply = async () => {
    const ops = [];
    GROUPS.forEach((g) => (diff[g.key] || []).forEach((it) => { if (selected.has(it.id)) ops.push(cleanOp(it)); }));
    if (ops.length === 0) return toast.error("Nessuna modifica selezionata");
    setLoading(true);
    try {
      const res = await client.post(`${base}/${menuId}/update-pdf/apply`, { ops });
      setDone(res.data.applied);
      setStep(3);
      toast.success("Menù aggiornato");
    } catch (e) { toast.error(errMsg(e, "Errore nell'applicazione")); }
    finally { setLoading(false); }
  };

  const Row = ({ it, children }) => (
    <label className="flex items-start gap-2.5 rounded-lg border border-border p-2.5 cursor-pointer hover:bg-muted/40" data-testid={`upd-item-${it.id}`}>
      <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggle(it.id)} data-testid={`upd-check-${it.id}`} className="mt-0.5 w-4 h-4 accent-[#002FA7] flex-shrink-0" />
      <div className="min-w-0 text-sm">{children}</div>
    </label>
  );

  const renderItem = (key, it) => {
    if (key === "prices") return <Row it={it}><span className="font-medium">{it.name}</span> <span className="text-muted-foreground">· {it.category}</span><div className="font-mono text-xs mt-0.5">{it.old} → <span className="font-semibold">{it.new}</span></div></Row>;
    if (key === "names") return <Row it={it}><span className="text-muted-foreground text-xs">{it.category}</span><div className="mt-0.5"><span className="line-through text-muted-foreground">{it.old_name}</span> → <span className="font-semibold">{it.new_name}</span></div>{it.keep_name && <div className="text-[11px] text-amber-600 mt-0.5">Nome tenuto in italiano (non tradotto)</div>}</Row>;
    if (key === "descriptions") return <Row it={it}><span className="font-medium">{it.name}</span> <span className="text-muted-foreground text-xs">· {it.category}</span><div className="text-xs text-muted-foreground mt-0.5 line-through line-clamp-2">{it.old_desc || "(vuota)"}</div><div className="text-xs mt-0.5 line-clamp-2">{it.new_desc}</div></Row>;
    if (key === "new") return <Row it={it}><span className="font-semibold">{it.name}</span> <span className="text-muted-foreground text-xs">· {it.category_name}</span>{(it.price != null || (it.formats || []).length > 0) && <span className="font-mono text-xs ml-1">{(it.formats || []).length ? it.formats.map((f) => `${f.label} €${f.price}`).join(" · ") : `€${it.price}`}</span>}{it.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{it.description}</div>}</Row>;
    if (key === "removed") return <Row it={it}><span className="font-medium">{it.name}</span> <span className="text-muted-foreground text-xs">· {it.category}</span> <span className="font-mono text-xs">{it.price}</span><div className="text-[11px] text-red-600 mt-0.5">Deseleziona per NON rimuovere</div></Row>;
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="pdf-update-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileUp className="w-5 h-5 text-[#002FA7]" /> Aggiorna menù da PDF{title ? ` · ${title}` : ""}</DialogTitle>
          <DialogDescription>Confronta un nuovo PDF col menù esistente e conferma solo le modifiche che vuoi applicare.</DialogDescription>
        </DialogHeader>

        {step === 0 && (
          <div className="py-2">
            <p className="text-sm text-muted-foreground mb-3">Scegli quale menù aggiornare. L'operazione tocca <b>solo</b> quel menù.</p>
            <div className="space-y-2">
              {menus.map((m) => (
                <button key={m.id} onClick={() => { setMenuId(m.id); setStep(1); }} data-testid={`upd-pick-menu-${m.id}`} className="w-full text-left rounded-xl border border-border px-4 py-3 hover:border-[#002FA7] hover:bg-[#002FA7]/5 font-medium">{m.name}</button>
              ))}
              {menus.length === 0 && <p className="text-sm text-muted-foreground">Nessun menù disponibile.</p>}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">Carica il <b>nuovo</b> PDF del menù. Confronteremo i piatti col menù attuale e ti mostreremo solo le modifiche da confermare. Foto, allergeni, porzioni e varianti già impostati restano intatti. Il PDF non verrà pubblicato.</p>
            <label className="flex flex-col items-center gap-2 border-2 border-dashed border-border rounded-2xl py-10 cursor-pointer hover:border-[#002FA7] transition-colors" data-testid="upd-dropzone">
              {loading ? <><Loader2 className="w-7 h-7 animate-spin text-[#002FA7]" /><span className="text-sm text-muted-foreground">Analisi e confronto in corso…</span></> : <><FileUp className="w-8 h-8 text-muted-foreground" /><span className="text-sm font-medium">Seleziona il nuovo PDF</span></>}
              <input type="file" accept="application/pdf" className="hidden" onChange={onFile} data-testid="upd-input" disabled={loading} />
            </label>
          </div>
        )}

        {step === 2 && diff && (
          <div className="space-y-4">
            {totalChanges === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="upd-no-changes">Nessuna differenza rilevata: il menù è già allineato al PDF.</p>
            ) : GROUPS.map((g) => {
              const items = diff[g.key] || [];
              if (items.length === 0) return null;
              const Icon = g.icon;
              return (
                <div key={g.key} data-testid={`upd-group-${g.key}`}>
                  <p className={`text-sm font-semibold flex items-center gap-1.5 mb-1.5 ${g.color}`}><Icon className="w-4 h-4" /> {g.label} ({items.length})</p>
                  <div className="space-y-1.5">{items.map((it) => <div key={it.id}>{renderItem(g.key, it)}</div>)}</div>
                </div>
              );
            })}
          </div>
        )}

        {step === 3 && done && (
          <div className="py-6 text-center" data-testid="upd-done">
            <p className="text-base font-semibold">Aggiornamento completato ✓</p>
            <p className="text-sm text-muted-foreground mt-2">{done.prices} prezzi · {done.names} nomi · {done.descriptions} descrizioni · {done.new} nuovi · {done.removed} rimossi. Le traduzioni sono state aggiornate solo dove necessario.</p>
          </div>
        )}

        <DialogFooter>
          {step === 2 && <button onClick={() => setStep(1)} className="rounded-full px-5 py-2 text-sm font-medium hover:bg-muted flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Indietro</button>}
          {step !== 3 && <button onClick={onClose} className="rounded-full px-5 py-2 text-sm font-medium hover:bg-muted">Annulla</button>}
          {step === 2 && totalChanges > 0 && <button onClick={apply} disabled={loading} data-testid="upd-apply-btn" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a] disabled:opacity-60 flex items-center gap-2">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Applica selezionate ({selected.size})</button>}
          {step === 3 && <button onClick={onApplied} data-testid="upd-done-close" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a]">Chiudi</button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PdfUpdateDialog;
